import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { BackButton, PageHeader, TableRowEditButton, ToastSeverity, useToast } from '@/ui'
import { useTranslation } from 'react-i18next'
import { usePluginInstanceQuery } from '@/layouts/app-connect/api/usePluginInstanceQuery'
import { usePluginQuery } from '@/layouts/app-connect/api'
import { Disable, Launch, Trash } from '@/icons'
import { APP_CONNECT_PATH, APP_CONNECT_PLUGIN_DETAIL_PATH } from '@/libs'
import { PluginConfigForm } from '@/layouts/app-connect/components/PluginConfigForm'
import { FormProvider, useForm } from 'react-hook-form'
import { CenteredProgress } from '@/components'
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalPresetConfirm,
  ModalTitle,
  SelectItem,
  Spinner,
  useModal,
} from '@dashboard/ui'
import { usePluginUninstallMutate } from '@/layouts/app-connect/api/usePluginUninstallMutate'
import { usePluginInstallMutate } from '@/layouts/app-connect/api/usePluginInstallMutate'
import { useEffect, useState } from 'react'
import { dashboardClient, getErrorText } from '@/api'
import { PluginStatus } from '@/layouts/app-connect/components/PluginStatus'
import { FieldListBlock, PluginPageBlock, PluginType } from '@/api/dashboard'
import { usePluginUpdateMutate } from '@/layouts/app-connect/api/usePluginUpdateMutate'
import { getUrlParams } from '@/auth/useAuthToken'

export default function PluginEditPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { companyId, gameId, pluginSlug } = useParams() as { companyId: string; gameId: string; pluginSlug: string }
  const showToast = useToast()
  const [isLaunched, setIsLaunched] = useState(false)
  const [isInstallInProgress, setIsInstallInProgress] = useState(false)
  const { data: pluginInstance, isFetching: isInstancePluginLoading } = usePluginInstanceQuery(
    companyId,
    gameId,
    pluginSlug,
  )
  const { data: plugin, isFetching: isPluginLoading } = usePluginQuery(companyId, gameId, pluginSlug)
  const { mutateAsync: uninstallMutateAsync } = usePluginUninstallMutate(companyId, gameId, pluginSlug)
  const { mutateAsync: createMutateAsync } = usePluginInstallMutate(companyId, gameId, pluginSlug)
  const { mutateAsync: updaeteMutateAsync } = usePluginUpdateMutate(companyId, gameId, pluginSlug)

  const methods = useForm({})

  const resetForm = () => {
    methods.reset(
      Object.keys(methods.watch()).reduce((acc, key) => {
        // @ts-ignore
        acc[key] = ''
        return acc
      }, {}),
    )
  }

  useEffect(() => {
    const defaultValues = (pluginInstance?.plugin_config || {}) as Record<string, unknown>
    const blocks = plugin?.plugin_config_schema?.layout || []

    const allFields = [
      ...blocks
        .filter(it => it.model_type == 'PluginPageBlock')
        .map(it => (it as PluginPageBlock).children)
        .flat()
        .filter(it => it.model_type == 'FieldListBlock')
        .map(it => (it as FieldListBlock).fields)
        .flat(),
      ...blocks
        .filter(it => it.model_type == 'FieldListBlock')
        .map(it => (it as FieldListBlock).fields)
        .flat(),
    ]

    for (const field of allFields) {
      if (field.value) {
        defaultValues[field.name] = field.value
      }
    }

    methods.reset(defaultValues, { keepDirty: false })

    if (plugin?.plugin_config_schema?.install_oauth_link) {
      const hash = window.location.hash.substring(1)
      const params = new URLSearchParams(hash)

      const accessToken = params.get('access_token') || getUrlParams()['access_token']
      if (accessToken) {
        if (accessToken !== 'undefined') {
          installFromOAuth2(accessToken)
        } else {
          closeOAuth2()
        }
      }
    }
  }, [pluginInstance, methods.reset, plugin])

  const closeOAuth2 = () => {
    navigate(
      generatePath(APP_CONNECT_PLUGIN_DETAIL_PATH, {
        companyId: companyId,
        gameId: gameId,
        pluginSlug: pluginSlug,
      }),
      { replace: true },
    )
  }

  const installFromOAuth2 = async (accessToken: string) => {
    try {
      if (isInstallInProgress) {
        return
      }

      setIsInstallInProgress(true)

      closeOAuth2()

      await doInstall(
        {},
        {
          access_token: accessToken,
        },
      )
    } finally {
      setIsInstallInProgress(false)
    }
  }

  const doInstall = async (data: object, secret_data: object | null = null) => {
    try {
      if (pluginInstance?.id) {
        await updaeteMutateAsync({
          update: {
            plugin_config: data,
          },
        })
      } else {
        await createMutateAsync({
          create: {
            plugin_config: data,
            secret_data: secret_data as Record<string, string>,
          },
        })
      }
      showToast({ message: t('plugin.install.toast'), severity: ToastSeverity.success })
    } catch (e) {
      openErrorInstall({ message: getErrorText(e) })
    }
  }

  const onInstall = async (data: object) => {
    if (plugin?.plugin_config_schema?.install_oauth_link) {
      window.location.href = plugin.plugin_config_schema.install_oauth_link
      return
    }

    await doInstall(data)
  }

  const onDisableClick = async () => {
    await updaeteMutateAsync({
      update: {
        enabled: !pluginInstance?.enabled,
      },
    })
    showToast({
      message: t(pluginInstance?.enabled ? 'plugin.disable.toast' : 'plugin.enable.toast'),
      severity: ToastSeverity.success,
    })
  }

  const onLaunchApp = async () => {
    setIsLaunched(true)
    try {
      await dashboardClient.v1.runPlugin(companyId, gameId, pluginSlug)
      showToast({ message: t('plugin.app-run-done.toast'), severity: ToastSeverity.success })
    } catch (e) {
      showToast({ message: getErrorText(e) || 'Something went wrong', severity: ToastSeverity.error })
    } finally {
      setIsLaunched(false)
    }
  }

  const onConfirmUninstall = async () => {
    await uninstallMutateAsync()
    showToast({ message: t('plugin.uninstall.toast'), severity: ToastSeverity.success })
    resetForm()
  }

  const openConfirmUninstall = useModal(props => (
    <ModalPresetConfirm
      {...props}
      title={{
        children: t('confirm.title'),
        subtitle: t('plugin.uninstall.confirm'),
      }}
      cancel={{
        label: t('Cancel'),
      }}
      submit={{
        color: 'danger',
        callback: () => {
          props.onClose()
          onConfirmUninstall()
        },
        label: t('plugin.uninstall'),
      }}
    />
  ))

  const openErrorInstall = useModal<{ message: string }>(props => (
    <Modal {...props}>
      <ModalContent>
        <ModalTitle subtitle={props.message}>{t('plugin.install.error')}</ModalTitle>
      </ModalContent>
      <ModalFooter>
        <Button size="sm" data-testid="confirm-cancel-button" onClick={props.onClose}>
          {t('close')}
        </Button>
      </ModalFooter>
    </Modal>
  ))

  const renderForm = () => {
    return (
      <FormProvider {...methods}>
        <PageHeader
          sticky={true}
          extra={
            <div className="flex items-center gap-3">
              {pluginInstance ? (
                <>
                  <Button
                    size="md"
                    loading={methods.formState.isSubmitting}
                    disabled={!methods.formState.isDirty}
                    onClick={methods.handleSubmit(doInstall)}
                  >
                    {t('plugin.update')}
                  </Button>

                  {isLaunched ? (
                    <Spinner size={16} className="text-text-tertiary" />
                  ) : (
                    <TableRowEditButton
                      onChange={v => {
                        switch (v) {
                          case 'uninstall':
                            openConfirmUninstall()
                            break
                          case 'run':
                            onLaunchApp()
                            break
                          case 'enable':
                            onDisableClick()
                            break
                        }
                      }}
                      options={
                        [
                          plugin?.type != PluginType.Analytics && {
                            value: 'run',
                            children: t('plugin.run'),
                            icon: Launch,
                          },
                          pluginInstance?.enabled
                            ? {
                                value: 'enable',
                                children: <span className="text-text-error-primary">{t('plugin.disable')}</span>,
                                icon: Disable,
                              }
                            : {
                                value: 'enable',
                                children: t('plugin.enable'),
                                icon: Disable,
                              },
                          {
                            value: 'uninstall',
                            variant: 'danger',
                            children: t('plugin.uninstall'),
                            icon: Trash,
                          },
                        ].filter(it => !!it) as SelectItem[]
                      }
                    />
                  )}
                </>
              ) : (
                <Button
                  size="sm"
                  loading={methods.formState.isSubmitting}
                  onClick={methods.handleSubmit(onInstall)}
                  disabled={isInstallInProgress}
                >
                  {t('plugin.install')}
                </Button>
              )}
            </div>
          }
        >
          <BackButton
            text={plugin?.name || ''}
            onClick={() =>
              navigate(
                generatePath(APP_CONNECT_PATH, {
                  companyId: companyId,
                  gameId: gameId,
                }),
              )
            }
            icon={
              <div
                className="size-[40px] bg-cover bg-center bg-no-repeat"
                style={{
                  backgroundImage: `url(${plugin?.image_url})`,
                }}
              />
            }
            extraRight={
              <div className="font-inter">
                <PluginStatus status={pluginInstance?.enabled} />
              </div>
            }
          />
        </PageHeader>
        {isInstallInProgress ? (
          <CenteredProgress />
        ) : (
          <>
            {plugin?.plugin_config_schema && !pluginInstance?.plugin_instance_config_schema && (
              <PluginConfigForm config={plugin.plugin_config_schema} pluginInstanceId={pluginInstance?.id} />
            )}
            {pluginInstance?.plugin_instance_config_schema && (
              <PluginConfigForm
                config={pluginInstance.plugin_instance_config_schema}
                pluginInstanceId={pluginInstance.id}
              />
            )}
          </>
        )}
      </FormProvider>
    )
  }

  return (
    <div className="flex h-full flex-col gap-[18px]">
      {isPluginLoading || isInstancePluginLoading ? <CenteredProgress /> : renderForm()}
    </div>
  )
}
